import isPermissionEnabled from "common/isPermissionEnabled"
import { SUPERADMIN_NAV_LINKS } from "components/SuperAdmin/superadminNav"
import { ROUTES } from "constants/routes"
import { hasPlanSocialLoginAccess } from "utils/planSocialLogins"
import { getLoginRedirect } from "utils/routeUtils"

const DASHBOARD_PERMISSION_IDS = [
  "dashboard_1",
  "dashboard_2",
  "dashboard_3",
  "dashboard_4",
  "dashboard_5",
  "dashboard_6",
  "dashboard_7",
  "dashboard_8",
  "dashboard_9",
]

const ALWAYS_ACCESSIBLE_PREFIXES = [
  "/profile",
  "/change-password",
  "/my-preference",
  "/export-requests",
]

/** Tenant with no subscription plan — settings + billing only (backend sends permissions: []). */
const NO_PLAN_TENANT_ACCESSIBLE_PREFIXES = [
  ROUTES.BILLING_DASHBOARD,
  ROUTES.BILLING_PLANS,
  ROUTES.BILLING_CHECKOUT,
  ROUTES.BILLING_INVOICES,
  ROUTES.BILLING_INVOICE_DETAILS,
]

const NO_PLAN_TENANT_DEFAULT_ROUTE = ROUTES.MY_PREFERENCE

/** When no sidebar module is allowed — always-accessible settings/profile screens. */
const getNoModuleAccessFallback = (ctx = {}) => {
  if (isPlatformSuperAdminContext(ctx)) {
    return ROUTES.SA_DASHBOARD
  }
  return ROUTES.MY_PREFERENCE
}

const normalizePath = pathname => (pathname || "").replace(/\/+$/, "") || "/"

const getStoredTenantId = () =>
  typeof localStorage !== "undefined" ? localStorage.getItem("tenantId") : null

/** Platform console session (not SA impersonating / logged into a tenant). */
const isPlatformSuperAdminContext = (ctx = {}) =>
  isSuperAdminSession(ctx) && !getStoredTenantId()

const perm =
  (categoryId, permissionId) =>
  ({ userRole, accountType, accountScope }) =>
    isPermissionEnabled(
      userRole,
      categoryId,
      permissionId,
      accountType,
      accountScope
    )

const hasDashboardAccess = ctx =>
  DASHBOARD_PERMISSION_IDS.some(id => perm(1, id)(ctx))

const hasFaqAccess = ctx =>
  perm(18, "faq_1")(ctx) || perm(54, "rider_faq_1")(ctx)

const hasTermsAccess = ctx =>
  perm(33, "term_condition_1")(ctx) || perm(55, "rider_term_condition_1")(ctx)

const hasPrivacyAccess = ctx =>
  perm(34, "privacy_policy_1")(ctx) ||
  perm(56, "rider_privacy_policy_1")(ctx)

const hasMenusAccess = ctx =>
  perm(4, "menus_1")(ctx) || perm(4, "item_tag_1")(ctx)

const hasCustomersAccess = ctx =>
  perm(7, "customersPermission_1")(ctx) ||
  perm(7, "customersPermission_5")(ctx)

const hasReportAccess = (permissionId) => ctx =>
  perm(16, "report_1")(ctx) || perm(16, permissionId)(ctx)

const hasGiftCardReportsAccess = ctx =>
  perm(38, "gift_card_usage_1")(ctx) || perm(37, "gift_card_1")(ctx)

const hasEstoreReportsAccess = ctx =>
  perm(45, "estore_report_1")(ctx)

const hasGranularIntegrationView = ctx => {
  if (
    Array.isArray(ctx?.userIntegrations) &&
    ctx.userIntegrations.some(
      int => int?.permissions?.tenant_integrations_1 === true
    )
  ) {
    return true
  }
  const map = ctx?.userRole?.integrationPermissions
  if (!map || typeof map !== "object" || Array.isArray(map)) {
    return false
  }
  return Object.values(map).some(
    entry =>
      entry &&
      typeof entry === "object" &&
      !Array.isArray(entry) &&
      entry.tenant_integrations_1 === true
  )
}

const hasIntegrationsAccess = ctx =>
  perm(48, "tenant_integrations_1")(ctx) || hasGranularIntegrationView(ctx)

const hasSocialAuthSettingsAccess = ctx =>
  perm(53, "social_auth_settings_1")(ctx) &&
  hasPlanSocialLoginAccess(ctx?.planSocialLogins)

/** Match sidebar + AppSettings page: full view, clear-menu-cache, or ERP opt-in. */
const hasAppSettingsAccess = ctx =>
  perm(32, "app_settings_1")(ctx) ||
  perm(4, "clear_menu_cache")(ctx) ||
  perm(32, "enable_erp_integration")(ctx)

const hasBillingAccess = ctx => {
  const noPlanTenant =
    Array.isArray(ctx?.userRole?.permissions) &&
    ctx.userRole.permissions.length === 0
  return noPlanTenant || perm(51, "billing_1")(ctx)
}

/** Sidebar order — first match wins for post-login / unauthorized redirects. */
const TENANT_NAV_ENTRIES = [
  { path: ROUTES.DASHBOARD, canAccess: hasDashboardAccess },
  { path: "/banners", canAccess: perm(2, "inAppContent_1") },
  { path: "/offer-banners", canAccess: perm(49, "offerBanner_1") },
  { path: "/menus/menu-setup", canAccess: hasMenusAccess },
  { path: "/menus/categories", canAccess: perm(4, "menus_1") },
  { path: "/menus/items", canAccess: perm(4, "menus_1") },
  { path: "/menus/modifiers", canAccess: perm(4, "menus_1") },
  { path: "/menus/modifier-groups", canAccess: perm(4, "menus_1") },
  { path: "/menus/combos", canAccess: perm(4, "menus_1") },
  { path: "/menus/item-groups", canAccess: perm(4, "menus_1") },
  { path: "/menus/item-tags", canAccess: perm(4, "item_tag_1") },
  { path: "/roles", canAccess: perm(3, "roles_1") },
  { path: "/branches", canAccess: perm(5, "branchLocations_1") },
  { path: "/users", canAccess: perm(6, "usersPermission_1") },
  { path: "/user-activity", canAccess: perm(36, "user_activity_1") },
  { path: "/customers", canAccess: hasCustomersAccess },
  { path: "/support-issues", canAccess: perm(17, "customer_support_1") },
  { path: "/support-tickets", canAccess: perm(17, "customer_support_1") },
  { path: "/countries", canAccess: perm(11, "country_1") },
  { path: "/regions", canAccess: perm(25, "region_1") },
  { path: "/cities", canAccess: perm(26, "city_1") },
  { path: "/zones", canAccess: perm(43, "zone_1") },
  { path: ROUTES.ORDERS, canAccess: perm(9, "order_1") },
  { path: "/f&b-orders", canAccess: perm(9, "order_1") },
  { path: "/vat-info", canAccess: perm(12, "tax_1") },
  { path: "/api-logs", canAccess: perm(13, "api_logs_1") },
  { path: "/discounts", canAccess: perm(14, "promotion_1") },
  { path: "/coupons", canAccess: perm(14, "promotion_1") },
  { path: "/coupons-group", canAccess: perm(14, "promotion_1") },
  { path: "/coupon-usage", canAccess: perm(14, "promotion_1") },
  { path: "/generate-bulk-coupons", canAccess: perm(14, "promotion_1") },
  { path: "/wallet-coupons", canAccess: perm(46, "coupon_redeem_1") },
  { path: "/app-notifications", canAccess: perm(15, "notification_1") },
  { path: "/push-notifications", canAccess: perm(15, "notification_1") },
  { path: "/sales-reports", canAccess: hasReportAccess("view_sales_reports") },
  { path: "/payments-reports", canAccess: hasReportAccess("view_payments_reports") },
  { path: "/wallet-reports", canAccess: hasReportAccess("view_wallet_reports") },
  { path: "/wallet-transfer-reports", canAccess: hasReportAccess("view_wallet_transfer_reports") },
  { path: "/wallet-purchase-reports", canAccess: hasReportAccess("view_wallet_purchase_reports") },
  { path: "/loyalty-points-reports", canAccess: hasReportAccess("view_loyalty_points_reports") },
  { path: "/business-tax-reports", canAccess: hasReportAccess("view_business_tax_reports") },
  { path: "/premium-gift-card-reports", canAccess: hasReportAccess("view_premium_gift_card_reports") },
  { path: "/e-store", canAccess: hasEstoreReportsAccess },
  { path: "/faq", canAccess: hasFaqAccess },
  { path: "/terms-and-conditions", canAccess: hasTermsAccess },
  { path: "/privacy-policy", canAccess: hasPrivacyAccess },
  { path: "/app-settings", canAccess: hasAppSettingsAccess },
  { path: "/referral-settings", canAccess: perm(19, "referral_1") },
  { path: "/loyalty-settings", canAccess: perm(20, "loyalty_1") },
  { path: "/wallet-settings", canAccess: perm(60, "wallet_settings_1") },
  { path: "/order-cancel-settings", canAccess: perm(50, "order_cancel_settings_1") },
  { path: "/delivery-setting", canAccess: perm(59, "delivery_settings_1") },
  { path: "/delivery-partner-settings", canAccess: perm(47, "delivery_partners_6") },
  { path: "/bookings", canAccess: perm(21, "booking_1") },
  { path: "/generate-qr-code", canAccess: perm(22, "qr_code_1") },
  { path: "/ingredients", canAccess: perm(23, "ingredient_1") },
  { path: "/allergens", canAccess: perm(24, "allergen_1") },
  { path: "/rating-questions", canAccess: perm(27, "rating_1") },
  { path: "/branch-ratings", canAccess: perm(27, "rating_1") },
  { path: "/api-key", canAccess: perm(28, "api_key_1") },
  { path: "/branch-sync", canAccess: perm(29, "branch_sync_1") },
  { path: "/menu-sync", canAccess: perm(30, "menu_sync_3") },
  { path: "/add-emails", canAccess: perm(31, "add_emails_1") },
  { path: "/static-maintenance", canAccess: perm(41, "static_maintenance_1") },
  { path: "/giftcard", canAccess: perm(37, "gift_card_1") },
  { path: "/giftcard-categories", canAccess: perm(37, "gift_card_1") },
  { path: "/giftcard-settings", canAccess: perm(40, "gift_card_settings_1") },
  { path: "/giftcard-reports", canAccess: hasGiftCardReportsAccess },
  { path: "/templates", canAccess: perm(37, "gift_card_1") },
  { path: "/Giftcard-Deep-Link", canAccess: perm(39, "dynamic_link_1") },
  { path: "/vehicles", canAccess: perm(35, "vehicle_1") },
  { path: "/brands", canAccess: perm(52, "brands_1") },
  { path: "/delivery-partners", canAccess: perm(47, "delivery_partners_1") },
  { path: "/settings/integrations", canAccess: hasIntegrationsAccess },
  { path: "/settings/social-auth", canAccess: hasSocialAuthSettingsAccess },
  { path: "/integrations", canAccess: hasIntegrationsAccess },
  { path: ROUTES.BILLING_DASHBOARD, canAccess: hasBillingAccess },
  { path: ROUTES.BILLING_PLANS, canAccess: hasBillingAccess },
  { path: ROUTES.BILLING_CHECKOUT, canAccess: hasBillingAccess },
  { path: ROUTES.BILLING_INVOICES, canAccess: hasBillingAccess },
]

const flattenSuperAdminNav = () => {
  const entries = []
  SUPERADMIN_NAV_LINKS.forEach(link => {
    if (link.permission) {
      entries.push({ path: link.path, permission: link.permission })
    }
    ;(link.children || []).forEach(child => {
      if (child.permission) {
        entries.push({ path: child.path, permission: child.permission })
      }
    })
  })
  return entries
}

const SUPERADMIN_NAV_ENTRIES = flattenSuperAdminNav()

const TENANT_ROUTE_RULES = TENANT_NAV_ENTRIES.map(entry => ({
  prefix: entry.path,
  canAccess: entry.canAccess,
}))

const SUPERADMIN_ROUTE_RULES = SUPERADMIN_NAV_ENTRIES.map(entry => ({
  prefix: entry.path,
  canAccess: perm(entry.permission.categoryId, entry.permission.permissionId),
}))

export const isSuperAdminSession = ({ accountType, accountScope } = {}) =>
  accountScope === "super_admin" || accountType === "superadmin"

/** Legacy bootstrap operator — full bypass (matches isPermissionEnabled). */
export const isLegacyBootstrapSuperAdmin = ({
  accountType,
  accountScope,
} = {}) => {
  if (localStorage.getItem("tenantId")) {
    return false
  }
  const scope =
    accountScope ??
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("accountScope")
      : null)
  return accountType === "superadmin" && scope !== "tenant"
}

export const getAuthContextFromStorage = () => {
  try {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")
    return {
      userRole: authUser?.role || authUser?.userRole || null,
      accountType:
        localStorage.getItem("accountType") || authUser?.accountType || null,
      accountScope:
        localStorage.getItem("accountScope") ||
        authUser?.accountScope ||
        "tenant",
      planSocialLogins:
        authUser?.planSocialLogins && typeof authUser.planSocialLogins === "object"
          ? authUser.planSocialLogins
          : { google: false, apple: false },
      userIntegrations: Array.isArray(authUser?.integrations)
        ? authUser.integrations
        : [],
    }
  } catch {
    return {
      userRole: null,
      accountType: null,
      accountScope: "tenant",
      planSocialLogins: { google: false, apple: false },
      userIntegrations: [],
    }
  }
}

const isResolvedRole = role =>
  role && !Array.isArray(role) && Array.isArray(role.permissions)

/** Prefer Redux role when loaded; fall back to persisted login payload for first paint. */
export const mergeAuthContext = (reduxLogin = {}) => {
  const stored = getAuthContextFromStorage()
  const reduxRole = reduxLogin?.userRole

  return {
    userRole: isResolvedRole(reduxRole) ? reduxRole : stored.userRole,
    accountType: reduxLogin?.accountType ?? stored.accountType,
    accountScope: reduxLogin?.accountScope ?? stored.accountScope,
    planSocialLogins:
      reduxLogin?.planSocialLogins ?? stored.planSocialLogins ?? {
        google: false,
        apple: false,
      },
    userIntegrations: Array.isArray(reduxLogin?.userIntegrations)
      ? reduxLogin.userIntegrations
      : stored.userIntegrations || [],
  }
}

const isAlwaysAccessible = pathname => {
  const normalized = normalizePath(pathname)
  return ALWAYS_ACCESSIBLE_PREFIXES.some(
    prefix => normalized === prefix || normalized.startsWith(`${prefix}/`)
  )
}

const getRouteRules = ctx =>
  isPlatformSuperAdminContext(ctx) ? SUPERADMIN_ROUTE_RULES : TENANT_ROUTE_RULES

const findRouteRule = (pathname, ctx) => {
  const normalized = normalizePath(pathname)
  const rules = getRouteRules(ctx).sort(
    (a, b) => b.prefix.length - a.prefix.length
  )
  return rules.find(rule => {
    const prefix = normalizePath(rule.prefix)
    return normalized === prefix || normalized.startsWith(`${prefix}/`)
  })
}

const hasRolePermissions = ctx =>
  isResolvedRole(ctx?.userRole) && ctx.userRole.permissions.length > 0

/** Plan-scoped auth returns an empty permissions array when the tenant has no active plan. */
export const isNoPlanTenantContext = ctx => {
  if (isPlatformSuperAdminContext(ctx)) {
    return false
  }
  return (
    Array.isArray(ctx?.userRole?.permissions) &&
    ctx.userRole.permissions.length === 0
  )
}

const isNoPlanAccessiblePath = pathname => {
  const normalized = normalizePath(pathname)
  return NO_PLAN_TENANT_ACCESSIBLE_PREFIXES.some(prefix => {
    const normalizedPrefix = normalizePath(prefix)
    return (
      normalized === normalizedPrefix ||
      normalized.startsWith(`${normalizedPrefix}/`)
    )
  })
}

export const canAccessPath = (pathname, ctx) => {
  const normalized = normalizePath(pathname)

  // My Preference is tenant-portal only.
  if (
    isPlatformSuperAdminContext(ctx) &&
    (normalized === ROUTES.MY_PREFERENCE ||
      normalized.startsWith(`${ROUTES.MY_PREFERENCE}/`))
  ) {
    return false
  }

  if (isAlwaysAccessible(pathname)) {
    return true
  }

  if (isNoPlanTenantContext(ctx)) {
    return isNoPlanAccessiblePath(pathname)
  }

  if (isLegacyBootstrapSuperAdmin(ctx)) {
    return normalized.startsWith("/superadmin")
  }

  // Role not hydrated yet (e.g. Redux still `[]`) — deny module routes until resolved.
  if (!isResolvedRole(ctx?.userRole)) {
    const rule = findRouteRule(normalizePath(pathname), ctx)
    return !rule
  }

  if (!hasRolePermissions(ctx)) {
    return false
  }

  if (isPlatformSuperAdminContext(ctx)) {
    if (!normalized.startsWith("/superadmin")) {
      return false
    }
  } else if (normalized.startsWith("/superadmin")) {
    return false
  }

  const rule = findRouteRule(normalized, ctx)
  if (!rule) {
    return true
  }
  return rule.canAccess(ctx)
}

export const getFirstAccessibleRoute = (ctx = {}) => {
  if (isNoPlanTenantContext(ctx)) {
    return NO_PLAN_TENANT_DEFAULT_ROUTE
  }

  const entries = isPlatformSuperAdminContext(ctx)
    ? SUPERADMIN_NAV_ENTRIES.map(entry => ({
        path: entry.path,
        canAccess: perm(
          entry.permission.categoryId,
          entry.permission.permissionId
        ),
      }))
    : TENANT_NAV_ENTRIES

  for (const entry of entries) {
    if (entry.canAccess(ctx)) {
      return entry.path
    }
  }

  return getNoModuleAccessFallback(ctx)
}

export const resolveAuthorizedRedirect = (requestedPath, rawCtx = {}) => {
  const ctx = mergeAuthContext(rawCtx)
  const normalized = normalizePath(requestedPath)
  if (canAccessPath(normalized, ctx)) {
    return normalized
  }
  const fallback = getFirstAccessibleRoute(ctx)
  // Avoid bounce when fallback equals the denied path (e.g. SA dashboard).
  return normalizePath(fallback) === normalized ? normalized : fallback
}

export const resolvePostLoginRedirect = (search, loginData = {}) => {
  const loginAccountScope = loginData?.accountScope || null
  const loginAccountType = loginData?.accountType || null
  const loginLooksLikePlatformSa =
    (loginAccountScope === "super_admin" ||
      loginAccountType === "superadmin") &&
    loginAccountScope !== "tenant" &&
    !loginData?.tenantId &&
    !loginData?.tenant?._id &&
    !loginData?.tenant?.id

  // Clear stale tenant impersonation before resolving routes for platform SA login.
  if (loginLooksLikePlatformSa && typeof localStorage !== "undefined") {
    localStorage.removeItem("tenantId")
  }

  const ctx = mergeAuthContext({
    userRole: loginData?.role || loginData?.userRole || null,
    accountType: loginAccountType,
    accountScope: loginAccountScope || "tenant",
    planSocialLogins: loginData?.planSocialLogins || null,
  })

  let requested = getLoginRedirect(search)

  if (
    isPlatformSuperAdminContext(ctx) &&
    (requested === ROUTES.DASHBOARD ||
      requested === "/" ||
      requested === ROUTES.LOGIN ||
      requested === ROUTES.SA_LOGIN ||
      requested === ROUTES.MY_PREFERENCE)
  ) {
    requested = ROUTES.SA_DASHBOARD
  }

  return resolveAuthorizedRedirect(requested, ctx)
}

export const getForbiddenRedirectPath = () => {
  const ctx = getAuthContextFromStorage()
  return getFirstAccessibleRoute(ctx)
}

/** @deprecated Prefer resolveAuthorizedRedirect — kept for imports migrating off /forbidden. */
export const getUnauthorizedRedirectPath = (ctx, currentPath) => {
  if (canAccessPath(currentPath, ctx)) {
    return null
  }
  const fallback = getFirstAccessibleRoute(ctx)
  return normalizePath(fallback) === normalizePath(currentPath) ? null : fallback
}
